import React, { useEffect, useState } from 'react';
import { hoursOf, errText } from './portalApi';

const btn = 'w-full min-h-[52px] rounded-xl text-lg font-semibold transition-colors';
const field = 'w-full min-h-[48px] rounded-xl border border-slate-300 bg-white px-3 text-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-400';

const PlaceChooser = ({ api, value, onChange }) => {
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  useEffect(() => { const t = setTimeout(() => api.projects(q).then(setList).catch(() => {}), 250); return () => clearTimeout(t); }, [q, api]);
  const fixed = [['warsztat', 'Warsztat'], ['magazyn', 'Magazyn'], ['inne', 'Inne']];
  const chip = (active) => `px-4 min-h-[48px] rounded-xl border text-base ${active ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-800 border-slate-300'}`;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {fixed.map(([id, label]) => (
          <button type="button" key={id} className={chip(value.place_type === 'category' && value.category === id)}
            onClick={() => onChange({ place_type: 'category', category: id })} data-testid={`portal-cat-${id}`}>{label}</button>
        ))}
        <button type="button" className={chip(value.place_type === 'unlisted')} onClick={() => onChange({ place_type: 'unlisted' })}
          data-testid="portal-unlisted-btn">Nie ma na liście</button>
      </div>
      {value.place_type === 'unlisted' ? (
        <div className="space-y-2">
          <input className={field} placeholder="Nazwa klienta (wymagane)" value={value.unlisted_client || ''}
            onChange={(e) => onChange({ ...value, unlisted_client: e.target.value })} data-testid="portal-unlisted-client" />
          <input className={field} placeholder="Adres (opcjonalnie)" value={value.unlisted_address || ''}
            onChange={(e) => onChange({ ...value, unlisted_address: e.target.value })} data-testid="portal-unlisted-address" />
        </div>
      ) : (
        <>
          <input className={field} placeholder="Szukaj: klient lub adres" value={q} onChange={(e) => setQ(e.target.value)} data-testid="portal-project-search" />
          <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="portal-project-list">
            {list.length === 0 && <p className="p-3 text-slate-500">Brak zleceń</p>}
            {list.map((p) => (
              <button type="button" key={p.id} onClick={() => onChange({ place_type: 'project', project_id: p.id })} data-testid={`portal-project-${p.id}`}
                className={`w-full text-left p-3 min-h-[56px] ${value.project_id === p.id ? 'bg-orange-50 border-l-4 border-orange-500' : 'bg-white'}`}>
                <span className="block font-semibold text-slate-900">{p.nazwa}</span>
                <span className="block text-sm text-slate-600">{p.klient}{p.miejscowosc ? ` · ${p.miejscowosc}` : ''}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const EntryForm = ({ api, me, initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial || { date: me.today, start: '07:00', end: '15:00', break_min: 0, note: '' });
  const [place, setPlace] = useState(initial ? { place_type: initial.place_type, project_id: initial.project_id, category: initial.category,
    unlisted_client: initial.unlisted_client, unlisted_address: initial.unlisted_address } : { place_type: null });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const hours = hoursOf(f.start, f.end, f.break_min);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setErr(''); setSaving(true);
    const body = { date: f.date, start: f.start, end: f.end, break_min: Number(f.break_min) || 0, note: f.note, ...place };
    try { initial ? await api.update(initial.id, body) : await api.create(body); onSaved(); }
    catch (e) { setErr(errText(e, 'Nie udało się zapisać')); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="portal-entry-form">
      <label className="block"><span className="text-slate-700 font-medium">Data</span>
        <input type="date" className={field} value={f.date} min={me.min_date} max={me.today} onChange={set('date')} data-testid="portal-date-input" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label><span className="text-slate-700 font-medium">Od</span><input type="time" className={field} value={f.start} onChange={set('start')} data-testid="portal-start-input" /></label>
        <label><span className="text-slate-700 font-medium">Do</span><input type="time" className={field} value={f.end} onChange={set('end')} data-testid="portal-end-input" /></label>
      </div>
      <label className="block"><span className="text-slate-700 font-medium">Przerwa (minuty)</span>
        <input type="number" min="0" inputMode="numeric" className={field} value={f.break_min} onChange={set('break_min')} data-testid="portal-break-input" /></label>
      <p className="text-xl text-slate-900" data-testid="portal-hours-preview">Razem: <strong>{hours ? `${hours.toLocaleString('pl-PL')} h` : '—'}</strong></p>
      <div><span className="text-slate-700 font-medium block mb-2">Miejsce pracy</span><PlaceChooser api={api} value={place} onChange={setPlace} /></div>
      <label className="block"><span className="text-slate-700 font-medium">Notatka (opcjonalnie)</span>
        <textarea className={`${field} py-2`} rows={2} value={f.note || ''} onChange={set('note')} data-testid="portal-note-input" /></label>
      {err && <p className="rounded-xl bg-red-50 text-red-700 p-3" data-testid="portal-form-error">{err}</p>}
      <button type="button" className={`${btn} bg-orange-500 text-white disabled:opacity-50`} disabled={saving || !hours || !place.place_type}
        onClick={submit} data-testid="portal-save-btn">{saving ? 'Zapisywanie...' : initial ? 'Zapisz zmiany' : 'Zapisz godziny'}</button>
      {onCancel && <button type="button" className={`${btn} bg-slate-100 text-slate-700`} onClick={onCancel} data-testid="portal-cancel-btn">Anuluj</button>}
    </div>
  );
};
